import dayjs, { Dayjs } from 'dayjs';
import {
  TimeRange,
  TimeRangeEdgeEnum
} from '@models/time-range-picker.model.ts';
import {
  DeviceGroupScheduleModel,
  WeekDayEnum
} from '@models/digital-signage/device-group-schedules.model.ts';
import {
  ConflictSourceEnum,
  DayAvailability,
  MinutesRange,
  OccupiedSlot,
  ScheduleDateErrorEnum,
  ScheduleDayPresetEnum,
  ScheduleFormValues,
  ScheduleOccupancy,
  ScheduleRuleFormValue,
  ScheduleTimeDisabledParams,
  ScheduleValidationResult
} from '@modules/digital-signage/model/playlist-schedule.model.ts';
import {
  DAY_MINUTES,
  MIN_FREE_SLOT,
  SCHEDULE_DAY_PRESETS,
  SCHEDULE_MINUTES_STEP,
  TIME_PICKER_VIEW,
  WEEK_DAYS
} from '@modules/digital-signage/utils/schedule.utils.ts';
import {
  doRangesOverlap,
  invertRanges,
  isTimeDisabled,
  mergeRanges,
  toMinutes
} from '@modules/digital-signage/utils/schedule-time.utils.ts';
import {
  formatDateKey,
  getLastDayDate,
  getScheduleDates,
  getWeekDaysInRange,
  intersectDates,
  toDate,
  toWeekDay
} from '@modules/digital-signage/utils/schedule-date.utils.ts';
import {
  getScheduleSlots,
  isSavedRange,
  isSavedStart
} from '@modules/digital-signage/utils/schedule-slots.utils.ts';

export const toDraftRange = ({
  start,
  end
}: TimeRange): MinutesRange | null => {
  if (!start?.isValid() || !end?.isValid()) return null;
  return { start: toMinutes(start), end: toMinutes(end, true) };
};

const getAllSlots = ({
  existing,
  draft
}: Pick<ScheduleOccupancy, 'existing' | 'draft'>): OccupiedSlot[] => [
  ...existing,
  ...draft
];

const getTodayPassedSlot = (now: Dayjs): OccupiedSlot => ({
  day: toWeekDay(now),
  start: 0,
  end:
    Math.ceil(toMinutes(now) / SCHEDULE_MINUTES_STEP) * SCHEDULE_MINUTES_STEP,
  source: ConflictSourceEnum.Existing
});

export const buildScheduleOccupancy = (
  {
    startDate,
    endDate,
    rules
  }: Pick<ScheduleFormValues, 'startDate' | 'endDate' | 'rules'>,
  schedules: DeviceGroupScheduleModel[],
  excludeId?: string | null,
  /** Saved dates of the edited schedule: keeping them (even past) is valid */
  saved?: Pick<ScheduleFormValues, 'startDate' | 'endDate'>,
  now: Dayjs = dayjs()
): ScheduleOccupancy => {
  const date = formatDateKey(now);
  const nowMinutes = toMinutes(now);
  const period = { start: toDate(startDate), end: toDate(endDate) };
  const isSavedStart =
    !!saved?.startDate && period.start === toDate(saved.startDate);
  const isSavedEnd = !!saved?.endDate && period.end === toDate(saved.endDate);
  const dateError =
    period.start && period.end && period.end < period.start
      ? ScheduleDateErrorEnum.EndBeforeStart
      : period.start && period.start < date && !isSavedStart
        ? ScheduleDateErrorEnum.StartInPast
        : period.end && period.end < date && !isSavedEnd
          ? ScheduleDateErrorEnum.EndInPast
          : null;
  const active = dateError
    ? null
    : intersectDates(period, { start: date, end: null });

  const existing = !active
    ? []
    : schedules
        .filter((schedule) => schedule._id !== excludeId)
        .flatMap((schedule) => {
          const common = intersectDates(active, getScheduleDates(schedule));
          const sharedDays = common ? getWeekDaysInRange(common) : [];

          return getScheduleSlots(schedule).flatMap(({ days, range }) =>
            days
              .filter(
                (day) =>
                  sharedDays.includes(day) &&
                  !(
                    common &&
                    getLastDayDate(day, common) === date &&
                    range.end <= nowMinutes
                  )
              )
              .map((day) => ({
                ...range,
                day,
                source: ConflictSourceEnum.Existing,
                ownerName: schedule.name,
                ownerId: schedule._id
              }))
          );
        });

  const allowedDays = active ? getWeekDaysInRange(active) : WEEK_DAYS;

  const draft = rules.flatMap((rule, ruleIndex) =>
    rule.timeRanges.flatMap((timeRange, rangeIndex) => {
      const range = toDraftRange(timeRange);
      return range && range.end > range.start
        ? rule.days.map((day) => ({
            ...range,
            day,
            source: ConflictSourceEnum.Draft,
            ruleIndex,
            rangeIndex
          }))
        : [];
    })
  );

  return {
    dateError,
    allowedDays,
    endedDays: dateError
      ? []
      : getWeekDaysInRange(period).filter((day) => !allowedDays.includes(day)),
    period,
    now,
    existing,
    draft
  };
};

export const isDayOutOfRange = (
  day: WeekDayEnum,
  {
    allowedDays,
    endedDays
  }: Pick<ScheduleOccupancy, 'allowedDays' | 'endedDays'>
): boolean => !allowedDays.includes(day) && !endedDays.includes(day);

export const getRangeOccupiedSlots = (
  occupancy: ScheduleOccupancy,
  days: WeekDayEnum[],
  ruleIndex: number,
  rangeIndex: number
): OccupiedSlot[] =>
  getAllSlots(occupancy).filter(
    (slot) =>
      days.includes(slot.day) &&
      !(slot.ruleIndex === ruleIndex && slot.rangeIndex === rangeIndex)
  );

export const getDayAvailability = (
  day: WeekDayEnum,
  slots: OccupiedSlot[]
): DayAvailability => {
  const segments = mergeRanges(slots.filter((slot) => slot.day === day));
  const freeMinutes =
    DAY_MINUTES -
    segments.reduce((sum, { start, end }) => sum + end - start, 0);
  return { freeMinutes, isFull: freeMinutes < MIN_FREE_SLOT, segments };
};

export const getRemainingDayAvailability = (
  day: WeekDayEnum,
  slots: OccupiedSlot[],
  occupancy: Pick<ScheduleOccupancy, 'period' | 'now'>
): DayAvailability => {
  const availability = getDayAvailability(day, slots);
  if (!isOnlyTodayLeft([day], occupancy)) return availability;
  const { freeMinutes } = getDayAvailability(day, [
    ...slots,
    getTodayPassedSlot(occupancy.now)
  ]);
  return { ...availability, freeMinutes, isFull: freeMinutes < MIN_FREE_SLOT };
};

/** The longest gaps free on every given day, ordered by time */
export const getFreeSlots = (
  days: WeekDayEnum[],
  slots: OccupiedSlot[],
  limit = 3
): MinutesRange[] =>
  invertRanges(slots.filter((slot) => days.includes(slot.day)))
    .filter(({ start, end }) => end - start >= MIN_FREE_SLOT)
    .sort((a, b) => b.end - b.start - (a.end - a.start))
    .slice(0, limit)
    .sort((a, b) => a.start - b.start);

export const isOnlyTodayLeft = (
  days: WeekDayEnum[],
  { period, now }: Pick<ScheduleOccupancy, 'period' | 'now'>
): boolean => {
  if (!period.end) return false;
  const date = formatDateKey(now);
  const todayDay = toWeekDay(now);
  return (
    days.includes(todayDay) &&
    getLastDayDate(todayDay, period) === date &&
    days.every(
      (day) => day === todayDay || (getLastDayDate(day, period) ?? '') < date
    )
  );
};

export const validateSchedule = (
  rules: ScheduleRuleFormValue[],
  occupancy: ScheduleOccupancy,
  savedSlotKeys: Set<string> = new Set()
): ScheduleValidationResult => {
  const allSlots = getAllSlots(occupancy);
  const nowMinutes = toMinutes(occupancy.now);

  const ruleResults = rules.map((rule, ruleIndex) => {
    const isTodayOnly = isOnlyTodayLeft(rule.days, occupancy);
    const ranges = rule.timeRanges.map((timeRange, rangeIndex) => {
      const range = toDraftRange(timeRange);
      const isInvalid = !!range && range.end <= range.start;
      const isPast =
        !!range &&
        isTodayOnly &&
        range.start < nowMinutes &&
        !isSavedRange(rule.days, range, savedSlotKeys) &&
        !(
          range.end > nowMinutes &&
          isSavedStart(rule.days, range.start, savedSlotKeys)
        );
      const isEarlier = (slot: OccupiedSlot) =>
        slot.source === ConflictSourceEnum.Existing ||
        (slot.ruleIndex as number) < ruleIndex ||
        (slot.ruleIndex === ruleIndex &&
          (slot.rangeIndex as number) < rangeIndex);

      return {
        isIncomplete: !range,
        isInvalid,
        isPast,
        conflicts:
          range && !isInvalid
            ? allSlots.filter(
                (slot) =>
                  rule.days.includes(slot.day) &&
                  isEarlier(slot) &&
                  doRangesOverlap(slot, range)
              )
            : []
      };
    });

    const conflicts = ranges.flatMap((range) => range.conflicts);
    const daysOutOfRange = rule.days.filter((day) =>
      isDayOutOfRange(day, occupancy)
    );
    const hasOnlyEndedDays =
      rule.days.length > 0 &&
      rule.days.every((day) => occupancy.endedDays.includes(day)) &&
      !rule.timeRanges.every((timeRange) => {
        const range = toDraftRange(timeRange);
        return !!range && isSavedRange(rule.days, range, savedSlotKeys);
      });
    const suggestionRangeIndex = ranges.findIndex(
      (range) => range.conflicts.length
    );

    return {
      hasNoDays: !rule.days.length,
      hasOnlyEndedDays,
      daysOutOfRange,
      ranges,
      conflicts,
      conflictDays: [...new Set(conflicts.map((slot) => slot.day))],
      suggestionRangeIndex,
      suggestions:
        suggestionRangeIndex < 0
          ? []
          : getFreeSlots(rule.days, [
              ...getRangeOccupiedSlots(
                occupancy,
                rule.days,
                ruleIndex,
                suggestionRangeIndex
              ),
              ...(isTodayOnly ? [getTodayPassedSlot(occupancy.now)] : [])
            ]),
      isBlocked:
        !rule.days.length ||
        hasOnlyEndedDays ||
        daysOutOfRange.length > 0 ||
        ranges.some(
          (r) => r.isIncomplete || r.isInvalid || r.isPast || r.conflicts.length
        )
    };
  });

  const blockingCount = ruleResults.filter((rule) => rule.isBlocked).length;
  return {
    isValid: !occupancy.dateError && rules.length > 0 && !blockingCount,
    blockingCount,
    dateError: occupancy.dateError,
    rules: ruleResults
  };
};

export const getForeignSlots = (
  { existing, draft }: Pick<ScheduleOccupancy, 'existing' | 'draft'>,
  ruleIndex: number
): OccupiedSlot[] => [
  ...existing,
  ...draft.filter((slot) => slot.ruleIndex !== ruleIndex)
];

export const getValidDraftRanges = (timeRanges: TimeRange[]): MinutesRange[] =>
  timeRanges.flatMap((timeRange) => {
    const range = toDraftRange(timeRange);
    return range && range.end > range.start ? [range] : [];
  });

export const isDayUnavailable = (
  day: WeekDayEnum,
  {
    allowedDays,
    endedDays
  }: Pick<ScheduleOccupancy, 'allowedDays' | 'endedDays'>,
  availability: DayAvailability[]
): boolean =>
  !allowedDays.includes(day) ||
  endedDays.includes(day) ||
  availability[day].isFull;

export const getPresetDays = (
  preset: ScheduleDayPresetEnum,
  selectedDays: WeekDayEnum[],
  occupancy: Pick<ScheduleOccupancy, 'allowedDays' | 'endedDays'>,
  availability: DayAvailability[]
): WeekDayEnum[] =>
  WEEK_DAYS.filter(
    (day) =>
      (selectedDays.includes(day) && occupancy.endedDays.includes(day)) ||
      (SCHEDULE_DAY_PRESETS[preset].includes(day) &&
        !isDayUnavailable(day, occupancy, availability))
  );

export const isScheduleTimeDisabled = ({
  value,
  view,
  edge,
  rangeIndex,
  ruleIndex,
  rule,
  occupancy
}: ScheduleTimeDisabledParams): boolean => {
  const nowMinutes = isOnlyTodayLeft(rule.days, occupancy)
    ? toMinutes(occupancy.now)
    : null;
  const slots = [
    ...getRangeOccupiedSlots(occupancy, rule.days, ruleIndex, rangeIndex),
    ...(edge === TimeRangeEdgeEnum.Start && nowMinutes !== null
      ? [{ start: 0, end: nowMinutes }]
      : [])
  ];
  const start = rule.timeRanges[rangeIndex]?.start;
  const startMinutes =
    edge === TimeRangeEdgeEnum.End && start ? toMinutes(start) : null;
  const check = (minutes: number) =>
    isTimeDisabled(minutes, edge, startMinutes, slots) ||
    (edge === TimeRangeEdgeEnum.End &&
      nowMinutes !== null &&
      (minutes || DAY_MINUTES) <= nowMinutes);

  if (view === TIME_PICKER_VIEW.Hours) {
    // An hour is disabled only when none of its minutes can be picked.
    const hour = value.hour() * 60;
    return Array.from(
      { length: 60 / SCHEDULE_MINUTES_STEP },
      (_, step) => hour + step * SCHEDULE_MINUTES_STEP
    ).every(check);
  }
  if (view === TIME_PICKER_VIEW.Minutes) return check(toMinutes(value));
  return false;
};
